import { Injectable, ElementRef, NgZone, inject } from '@angular/core';
import {
  PlanBlockLetterResponseDto,
  CatalogPlanSeatResponseDto,
  SeatTypeEnumResponseDto,
  SeatStatusEnumResponseDto,
  EventSessionPlanShapeResponseDto,
} from '@core/services/apiclient';
import { ChartsUtils } from '@shared/components/charts/charts.utils';
import Konva from 'konva';
import { RectConfig } from 'konva/lib/shapes/Rect';
import { Vector2d } from 'konva/lib/types';
import * as _ from 'underscore';
import { clone } from 'underscore';
import { CatalogPlanBlockViewMinimapService } from './catalog-plan-blockView-minimap.service';
import { CanvasProps, SeatSelection } from '../models/catalog-plan.model';
import {
  BlockedSeatIcon,
  editedIcon,
  PaidIcon,
  reservedIcon,
  seatsChunkSize,
  VectorIcon,
  VerifiedIcon,
} from '../models/catalog-plan-constants';
import { BlockViewData } from '@core/models/catalog-plan.model';
import { BehaviorSubject } from 'rxjs';
import { polygonContains } from 'd3';

@Injectable({
  providedIn: 'root',
})
export class CatalogPlanBlockViewUtilsService {
  private readonly minimap = inject(CatalogPlanBlockViewMinimapService);
  private readonly ngZone = inject(NgZone);

  public data: BlockViewData;
  // these object are mandatory for performance sake
  private seatNodeMap: { [key: string]: Konva.Group };
  private parentGroupMap: { [key: string]: Konva.Group };
  private iconsMap: {
    blockedSeatImage?: HTMLImageElement;
    reservedSeatImage?: HTMLImageElement;
    paidSeatImage?: HTMLImageElement;
    editedSeatImage?: HTMLImageElement;
    controlledSeatImage?: HTMLImageElement;
    subscribedSeatImage?: HTMLImageElement;
    [key: string]: HTMLImageElement;
  };
  private seatSize: number;
  private shapesMap: {
    rect: Konva.Rect;
    circle: Konva.Rect;
    number: Konva.Label;
    selectionHighlighter: Konva.Rect;
  };

  private loaderInfo$ = new BehaviorSubject<{ show: boolean; progress?: number; description?: string }>({
    show: false,
    progress: 0,
    description: '',
  });

  /**
   * initialize some variables that our service needs
   */
  public async init(data: BlockViewData, seatSize: number) {
    this.data = clone(data);
    this.seatNodeMap = {};
    this.parentGroupMap = {};
    this.iconsMap = {};
    this.seatSize = seatSize;
    await this.prepareShapeCache();
  }

  /**
   * seat node map is an object that contains all the seat canvas nodes. Useful in the search
   */
  private pushToSeatNodeMap(seatId: number, node: Konva.Group): void {
    this.seatNodeMap[seatId] = node;
  }

  /**
   * create the group that contains the seats chunk. This parent group will be cached to improve performance
   */
  public createCatalogPlanSeats(
    seatsChunk: CatalogPlanSeatResponseDto[],
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
    statusDictionary: Map<string, Konva.Group>,
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    unavailabilityDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    const catalogPlanSeats = new Konva.Group({
      id: `catalog-parent-seat-group`,
    });

    this.batchDrawProcess(seatsChunk, scaleX, scaleY, ({ seat, scaleX, scaleY }) => {
      const seatGroup = this.createSeatGroup(
        {
          seat,
          scaleX,
          scaleY,
        },
        statusDictionary,
        categoryDictionary,
        contingentDictionary,
        unavailabilityDictionary,
        categoryLayer,
        contingentLayer,
      );
      catalogPlanSeats.add(seatGroup);
    }).then(() => {
      this.loaderInfo$.next({
        show: false,
        progress: 100,
        description: 'catalogPlan.planLoaded',
      });
      catalogPlanSeats.setAttrs({
        width:
          catalogPlanSeats.children?.[catalogPlanSeats.children?.length - 1]?.x() -
          catalogPlanSeats.children?.[0]?.x(),
        height:
          catalogPlanSeats.children?.[catalogPlanSeats.children?.length - 1]?.y() -
          catalogPlanSeats.children?.[0]?.y(),
      });
    });
    return catalogPlanSeats;
  }

  private batchDrawProcess(
    seats: CatalogPlanSeatResponseDto[],
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
    drawSeatInBatchProjection: (options: {
      seat: CatalogPlanSeatResponseDto;
      scaleX: (position: number) => number;
      scaleY: (position: number) => number;
    }) => void,
  ): Promise<void[]> {
    if (typeof Worker !== 'undefined') {
      const batchSize = seatsChunkSize;
      const numberOfSeats = seats.length;
      const numberOfWorkers = Math.ceil(numberOfSeats / batchSize);
      const promises: Promise<any>[] = [];
      let completedWorkers = 0;

      for (let i = 0; i < numberOfWorkers; i++) {
        const start = i * batchSize;
        const end = Math.min((i + 1) * batchSize, numberOfSeats);
        const partitionOfArray = seats.slice(start, end);
        const workerPromise = new Promise<void>((resolve, reject) => {
          const worker = new Worker(new URL('./../catalog-plan.worker', import.meta.url));

          worker.onmessage = ({ data }: MessageEvent<CatalogPlanSeatResponseDto[]>) => {
            this.ngZone.run(() => {
              data.forEach((seat) => {
                drawSeatInBatchProjection({
                  seat,
                  scaleX,
                  scaleY,
                });
              });
              completedWorkers++;
              this.updateProgressBarInfo(completedWorkers, numberOfWorkers);
              resolve();
            });
          };

          worker.onerror = reject;

          worker.postMessage(partitionOfArray);
        });

        promises.push(workerPromise);
      }

      return Promise.all(promises);
    } else {
      seats.forEach((seat) => {
        drawSeatInBatchProjection({
          seat,
          scaleX,
          scaleY,
        });
      });

      return Promise.resolve([]);
    }
  }

  updateProgressBarInfo(completedWorkers: number, totalWorkers: number) {
    const progress = Math.floor((completedWorkers / totalWorkers) * 100);
    this.loaderInfo$.next({
      show: true,
      progress: progress,
      description: progress === 0 ? 'catalogPlan.initialPlanLoading' : 'catalogPlan.planSeatLoadingProgress',
    });
  }

  public isLoaderDisplayed() {
    return this.loaderInfo$.asObservable();
  }

  /**
   * Vrai tant que les workers n'ont pas fini de livrer leurs places. Lecture synchrone, pour les
   * appelants qui doivent decider immediatement (l'export PNG refuse un plan a moitie dessine).
   */
  public get isDrawingSeats(): boolean {
    return this.loaderInfo$.value.show;
  }
  /**
   * creates the group that contains all the seat elements: seat box, seat number, selection highlighter
   */

  private createSeatGroup(
    options: {
      seat: CatalogPlanSeatResponseDto;
      scaleX: (position: number) => number;
      scaleY: (position: number) => number;
    },
    statusDictionary: Map<string, Konva.Group>,
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    unavailabilityDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    const seatGroup = this.initializeGroupNode(
      options.seat,
      options.scaleX(options.seat?.positionX),
      options.scaleY(options.seat?.positionY),
    );

    if (Math.abs(options.seat?.rotation) > 0) {
      seatGroup.offsetX(this.seatSize / 2);
      seatGroup.offsetY(this.seatSize / 2);
      seatGroup.rotate(options.seat?.rotation);
    }
    if (
      !options.seat?.seatUnavailabilityType ||
      (options.seat?.seatUnavailabilityType && this.isSeatLocked(options.seat))
    ) {
      this.drawSeatIcons(options.seat, seatGroup, statusDictionary);
    }
    this.initializeSeatInDictionaries(
      options.seat,
      seatGroup,
      categoryDictionary,
      contingentDictionary,
      unavailabilityDictionary,
      categoryLayer,
      contingentLayer,
    );

    const seatBox = this.drawSeatBox(options.seat);

    if (this.data?.blockId) {
      this.minimap.addMinimapElement(seatBox, seatGroup?.x(), seatGroup?.y());
    }
    seatGroup.add(seatBox);

    seatGroup.add(this.drawSeatNumber(options.seat));
    return seatGroup;
  }

  createNewSeatInDictionaries(
    options: {
      seat: CatalogPlanSeatResponseDto;
      scaleX: (position: number) => number;
      scaleY: (position: number) => number;
    },
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    statusDictionary: Map<string, Konva.Group>,
    unavailabilityDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    const seatGroup = this.initializeGroupNode(
      options.seat,
      options.scaleX(options.seat?.positionX),
      options.scaleY(options.seat?.positionY),
    );
    if (
      !options.seat?.seatUnavailabilityType ||
      (options.seat?.seatUnavailabilityType && this.isSeatLocked(options.seat))
    ) {
      this.drawSeatIcons(options.seat, seatGroup, statusDictionary);
    }
    this.initializeSeatInDictionaries(
      options.seat,
      seatGroup,
      categoryDictionary,
      contingentDictionary,
      unavailabilityDictionary,
      categoryLayer,
      contingentLayer,
    );
  }

  initializeGroupNode(seat: CatalogPlanSeatResponseDto, scaleX: number, scaleY: number) {
    const seatGroupNode = new Konva.Group({
      width: this.seatSize,
      height: this.seatSize,
      id: `catalog-seat-group-${seat?.seatId}`,
      x: scaleX,
      y: scaleY,
      rotation: seat?.rotation,
    });
    return seatGroupNode;
  }

  initializeSeatInDictionaries(
    seat: CatalogPlanSeatResponseDto,
    seatGroup: Konva.Group,
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    unavailabilityDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    const processGroup = (
      groupKey: string,
      dictionary: Map<string, Konva.Group>,
      layer: Konva.Layer,
      configureSeat: (clonedSeatGroup: Konva.Group) => void,
    ) => {
      let group = dictionary.get(groupKey);
      if (!group) {
        group = new Konva.Group({ id: groupKey });
        dictionary.set(groupKey, group);
        layer.add(group);
      }

      const clonedSeatGroup = seatGroup.clone();
      configureSeat(clonedSeatGroup);
      group.add(clonedSeatGroup);
      layer.batchDraw();
    };
    if (!this.isSeatLocked(seat)) {
      if (!seat.seatUnavailabilityType) {
        const categoryId = seat?.seatCategory?.id;
        if (categoryId != null) {
          processGroup(
            `catalog-seat-category-${categoryId}`,
            categoryDictionary,
            categoryLayer,
            (clonedSeatGroup) => {
              const seatBox = this.getShapeConfig(seat, true);
              clonedSeatGroup.add(seatBox);
              categoryLayer.batchDraw();
            },
          );
        }

        const contingentId = seat?.seatContingent?.id;
        if (contingentId != null) {
          processGroup(
            `catalog-seat-contingent-${contingentId}`,
            contingentDictionary,
            contingentLayer,
            (clonedSeatGroup) => {
              clonedSeatGroup.add(this.drawSeatHighlighter(seat));
              const seatBox = this.getShapeConfig(seat, false);
              clonedSeatGroup.add(seatBox);
              this.pushToSeatNodeMap(seat.seatId, clonedSeatGroup);
              this.appendContingentIconToSeat(seat, clonedSeatGroup);
            },
          );
        }
      } else {
        processGroup(
          `catalog-seat-unavailability-${seat.seatUnavailabilityType.id}`,
          unavailabilityDictionary,
          contingentLayer,
          (clonedSeatGroup) => {
            clonedSeatGroup.add(this.drawSeatHighlighter(seat));
            const seatBox = this.getShapeConfig(seat, false);
            clonedSeatGroup.add(seatBox);
            this.drawSeatUnavailabilityTypeLabel(seat, clonedSeatGroup);
            this.pushToSeatNodeMap(seat.seatId, clonedSeatGroup);
          },
        );
      }
    }
  }

  getShapeConfig(seat: CatalogPlanSeatResponseDto, isOnlyFill: boolean) {
    switch (seat?.typeSeat) {
      case SeatTypeEnumResponseDto.Normal:
        return this.shapesMap?.rect.clone({
          id: `catalog-seat-box-${seat?.seatId}`,
          name: `catalog-seat-box-${seat?.seatId}`,
          ...this.updateSeatColors(seat, isOnlyFill),
        });
      case SeatTypeEnumResponseDto.FoldingSeat:
        return this.shapesMap?.circle.clone({
          id: `catalog-seat-box-${seat?.seatId}`,
          name: `catalog-seat-box catalog-seat-box-${seat?.seatId}`,
          ...this.updateSeatColors(seat, isOnlyFill),
        });
    }
  }

  updateSeatColors(seat: CatalogPlanSeatResponseDto, isOnlyFill: boolean): Partial<RectConfig> {
    if (seat?.seatUnavailabilityType) {
      return {
        fill: ChartsUtils.getColorByPropertyName('--color-seat-disabled'),
        strokeWidth: 0,
      };
    }
    const fillColor = seat?.seatCategory?.color;
    const strokeColor = seat?.seatContingent?.color;

    return {
      fill: isOnlyFill ? fillColor : undefined,
      stroke: !isOnlyFill ? strokeColor : undefined,
      strokeWidth: !isOnlyFill && strokeColor ? 1 : 0,
    };
  }

  private drawSeatLockedImage(seat: CatalogPlanSeatResponseDto, seatGroup: Konva.Group) {
    if (this.isSeatLocked(seat)) {
      seatGroup.add(this.drawSeatIcon(seat, this.iconsMap?.blockedSeatImage));
    }
  }

  public drawSeatIcons(
    seat: CatalogPlanSeatResponseDto,
    seatGroup: Konva.Group,
    statusDictionary: Map<string, Konva.Group>,
  ) {
    this.drawSeatLockedImage(seat, seatGroup);
    if (seat?.status?.length) {
      const clonedSeatGroup = seatGroup.clone();
      seat.status.forEach((status) => {
        const statusIcon = this.drawSeatIcon(
          seat,
          this.getSeatIcon(status),
          this.getIconPosition(status),
          status,
        );
        if (!!statusIcon) {
          const statusGroup = statusDictionary.get(status);
          clonedSeatGroup.add(statusIcon);
          statusGroup.add(clonedSeatGroup);
        }
      });
    }
  }

  private getSeatIcon(status: SeatStatusEnumResponseDto) {
    switch (status) {
      case SeatStatusEnumResponseDto.Locked:
        return this.iconsMap?.blockedSeatImage;
      case SeatStatusEnumResponseDto.Reserved:
        return this.iconsMap?.reservedSeatImage;
      case SeatStatusEnumResponseDto.Paid:
        return this.iconsMap?.paidSeatImage;
      case SeatStatusEnumResponseDto.Edited:
        return this.iconsMap?.editedSeatImage;
      case SeatStatusEnumResponseDto.Controlled:
        return this.iconsMap?.controlledSeatImage;
      case SeatStatusEnumResponseDto.Subscribed:
        return this.iconsMap?.subscribedSeatImage;
    }
  }

  private drawSeatBox(seat: CatalogPlanSeatResponseDto) {
    switch (seat?.typeSeat) {
      case SeatTypeEnumResponseDto.Normal:
        return this.shapesMap?.rect.clone({
          id: `seat-box-${seat?.seatId}`,
          name: `seat-box-${seat?.seatId} catalog-seat-unavailability-${seat?.seatUnavailabilityType?.id}  catalog-seat-category-${seat?.seatCategory?.id} catalog-seat-contingent-${seat?.seatContingent?.id}`,
          fill: ChartsUtils.getColorByPropertyName('--color-seat-disabled'),
          strokeWidth: 0,
        });
      case SeatTypeEnumResponseDto.FoldingSeat:
        return this.shapesMap?.circle.clone({
          id: `seat-box-${seat?.seatId}`,
          name: `seat-box-${seat?.seatId} catalog-seat-unavailability-${seat?.seatUnavailabilityType?.id}  catalog-seat-category-${seat?.seatCategory?.id} catalog-seat-contingent-${seat?.seatContingent?.id}`,
          fill: ChartsUtils.getColorByPropertyName('--color-seat-disabled'),
          strokeWidth: 0,
        });
    }
  }

  public selectOneSeat(seat: SeatSelection) {
    if (!this.isSeatLocked(seat?.data)) {
      return { ...seat, seatId: seat?.data?.seatId };
    }
  }

  public selectSeats(contingentLayer: Konva.Layer, selectionArea: Konva.Rect | Konva.Line) {
    let selectionClone = [];
    this.getAreaAndStageIntersection(contingentLayer, selectionArea)
      .map((group: Konva.Group) =>
        this.getSeatByPosition(group.findOne((shape) => shape instanceof Konva.Shape)),
      )
      .forEach(({ node, data }) => {
        if (data) {
          const selectedSeat = this.selectOneSeat({ node, data });
          if (selectedSeat) {
            selectionClone = [...selectionClone, selectedSeat];
          }
        }
      });
    return selectionClone;
  }

  /**
   * when doing multiple selection, this function will determine the intersection between the stage
   * and the selection shape (rect or lasso)
   * and checks if there are seats included in this intersection
   */
  public getAreaAndStageIntersection(layer: Konva.Layer, selectionArea: Konva.Rect | Konva.Line) {
    const selectionAreaPoints = this.getShapePoints(selectionArea);
    return this.getAllSeatGroups(layer).filter(
      (group: Konva.Group) =>
        this.isShapeWithinArea(this.getShapePoints(group), selectionAreaPoints) &&
        this.isSelectableShape(group),
    );
  }

  private getAllSeatGroups(contingentLayer: Konva.Layer) {
    return (contingentLayer.children || []).reduce(
      (acc: Konva.Group[], curr: Konva.Group) => [...acc, ...(curr.children || [])],
      [],
    );
  }

  private isShapeWithinArea(shapePoints: [number, number][], areaPoints: [number, number][]): boolean {
    return shapePoints?.filter((point) => polygonContains(areaPoints, point))?.length >= 2;
  }

  public isSelectableShape(group: Konva.Group) {
    if (!group.isVisible()) {
      return false;
    }

    const rect = group.findOne(
      (child) => child instanceof Konva.Rect && child.id().startsWith('catalog-seat-box-'),
    ) as Konva.Rect;
    return rect?.isVisible() ?? false;
  }

  private drawSeatNumber(seat: CatalogPlanSeatResponseDto) {
    const seatNumber: Konva.Label = this.shapesMap?.number.clone({
      x: 0,
      y: this.seatSize + 1,
      id: `catalog-seat-number-${seat?.seatId}`,
    });
    seatNumber.getText().text(`${seat?.seatNumber}`);
    const textWidth = seatNumber.getWidth();
    seatNumber.setAttr('x', (this.seatSize - textWidth) / 2);
    return seatNumber;
  }

  public drawSeatHighlighter(seat: CatalogPlanSeatResponseDto) {
    switch (seat?.typeSeat) {
      case SeatTypeEnumResponseDto.Normal:
        return this.shapesMap?.selectionHighlighter.clone({
          id: `catalog-seat-highlighter-${seat?.seatId}`,
          cornerRadius: 2,
        });
      case SeatTypeEnumResponseDto.FoldingSeat:
        return this.shapesMap?.selectionHighlighter.clone({
          id: `catalog-seat-highlighter-${seat?.seatId}`,
          cornerRadius: this.seatSize,
        });
    }
  }

  public drawSeatIcon(
    seat: CatalogPlanSeatResponseDto,
    image: HTMLImageElement,
    position?: string,
    status?: SeatStatusEnumResponseDto,
  ) {
    return new Konva.Image({
      id: `catalog-seat-image-${seat?.seatId}`,
      name: status ? `${status}` : '',
      image,
      x: position ? this.seatSize - 4 : 1,
      y: this.getIconPositionY(position),
      width: position ? 8 : this.seatSize - 2,
      height: position ? 8 : this.seatSize - 2,
      perfectDrawEnabled: false,
      listening: false,
      visible: status ? status !== SeatStatusEnumResponseDto.Subscribed : true,
    });
  }

  private getIconPositionY(position) {
    if (position === 'top-right') {
      return -2;
    } else if (position === 'bottom-right') {
      return this.seatSize - 4;
    }
    return 1;
  }

  private async appendContingentIconToSeat(seat: CatalogPlanSeatResponseDto, group: Konva.Group) {
    if (this.seatHasImage(seat)) {
      group.add(this.drawSeatIcon(seat, this.iconsMap?.[seat?.seatContingent?.id]));
    }
  }

  public seatHasImage(seat: CatalogPlanSeatResponseDto) {
    return !!this.data?.contingents.find((r) => r?.id === seat?.seatContingent?.id)?.image;
  }

  public isSeatLocked(seat: CatalogPlanSeatResponseDto) {
    return seat?.status.some((s) => s === SeatStatusEnumResponseDto.Locked);
  }

  drawSeatUnavailabilityTypeLabel(seat: CatalogPlanSeatResponseDto, group: Konva.Group) {
    const firstLettre = seat.seatUnavailabilityType?.label?.slice(0, 1);
    const text = new Konva.Text({
      text: firstLettre,
      fontSize: 8,
      id: `catalog-seat-unavailability-text-${seat?.seatId}`,
      name: `catalog-seat-unavailability-text`,
      align: 'centre',
      fontStyle: 'bold',
      perfectDrawEnabled: false,
      listening: false,
    });
    text.x((this.seatSize - text.width()) / 2);
    text.y((this.seatSize - text.height()) / 2);
    group.add(text);
  }

  getIconPosition(status: SeatStatusEnumResponseDto) {
    switch (status) {
      case SeatStatusEnumResponseDto.Reserved:
      case SeatStatusEnumResponseDto.Paid:
      case SeatStatusEnumResponseDto.Edited:
        return 'top-right';
      case SeatStatusEnumResponseDto.Subscribed:
        return 'bottom-right';
    }
  }

  public drawLetter(
    letter: PlanBlockLetterResponseDto,
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
  ) {
    const text = new Konva.Text({
      x: scaleX(letter?.x),
      y: scaleY(letter?.y),
      text: letter?.label,
      fill: letter?.color,
      fontFamily: letter?.font,
      fontSize: letter?.fontSize,
      id: `${letter?.label}`,
      align: 'left',
      perfectDrawEnabled: false,
      listening: false,
    });

    if (letter?.rotation) {
      this.rotateAroundCenter(text, letter);
    }
    return text;
  }

  public drawImage(
    props: { width: number; height: number; x?: number; y?: number; rotation?: number },
    image: HTMLImageElement,
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
  ) {
    return new Konva.Image({
      image,
      x: scaleX(props?.x),
      y: scaleY(props?.y),
      width: props?.width,
      height: props?.height,
      rotation: props?.rotation,
      perfectDrawEnabled: false,
      listening: false,
    });
  }

  public drawShape(
    data: EventSessionPlanShapeResponseDto,
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
  ) {
    //opacity must be between 0 and 1
    const shapeOpacity: number = Math.min(1, Math.max(0, data?.opacity / 100));

    const originPoint = {
      x: data?.positionX,
      y: data?.positionY,
    };

    if (data?.width < 0) {
      originPoint.x = originPoint.x + data?.width;
    }

    if (data?.height < 0) {
      originPoint.y = originPoint.y + data?.height;
    }

    // https://konvajs.org/api/Konva.Shape.html
    const shape = this.shapesMap?.rect.clone({
      x: scaleX(originPoint.x),
      y: scaleY(originPoint.y),
      width: Math.abs(data?.width),
      height: Math.abs(data?.height),
      fill: data?.fillColorFrame,
      stroke: data?.fillColorfilling,
      rotation: data.rotation,
      opacity: shapeOpacity,
      cornerRadius: data?.radiusEdges,
    });

    return shape;
  }

  public getSeatById(seatId: number) {
    return this.seatNodeMap?.[`${seatId}`];
  }

  public getSeatByPosition(node: Konva.Stage | Konva.Shape): {
    data: CatalogPlanSeatResponseDto;
    node?: Konva.Stage | Konva.Group;
    pointerPosition?: Vector2d;
  } {
    if (node) {
      if (node instanceof Konva.Group) {
        const { x, y } = node.getAbsolutePosition();
        const pointerPosition = { x: x + node.width(), y };
        const seatId = node.id().slice('catalog-seat-group-'.length);
        const data = this.data?.seats.find((element) => `${element?.seatId}` === seatId);
        return {
          node,
          pointerPosition,
          data,
        };
      }
      return this.getSeatByPosition(node.getParent());
    }
    return {
      data: null,
    };
  }

  public highlightSelectedNodes(selection: SeatSelection[], seatLayer: Konva.Layer) {
    selection.forEach(({ node, seatId, data }) => {
      if (node) {
        this.setGroupActiveState(node, seatId, data?.typeSeat, seatLayer);
      }
    });
  }

  public restoreSelectedNodes(selection: SeatSelection[], seatLayer: Konva.Layer) {
    // restore seat box dimensions
    selection.forEach(({ node, seatId, data }) => {
      this.setGroupDefaultState(node, seatId, data?.typeSeat, seatLayer);
    });
  }

  private setGroupActiveState(
    group: Konva.Group,
    seatId: number,
    seatType: SeatTypeEnumResponseDto,
    seatLayer: Konva.Layer,
  ) {
    if (group) {
      const seatBox: Konva.Rect = group.findOne(`#catalog-seat-box-${seatId}`);
      const seatNumber: Konva.Label = seatLayer.findOne(`#catalog-seat-number-${seatId}`);
      const seatHighlighter: Konva.Label = group.findOne(`#catalog-seat-highlighter-${seatId}`);
      if (seatBox) {
        if (seatNumber) {
          seatNumber.getTag().setAttrs({
            fill: ChartsUtils.getColorByPropertyName('--color-primary-600'),
          });
        }
        if (seatHighlighter) {
          seatHighlighter.setAttrs({
            visible: true,
            name: 'highlighter-selected',
          });
        }
        seatBox.setAttrs({
          width: this.seatSize - 2.5,
          height: this.seatSize - 2.5,
          x: 1.25,
          y: 1.25,
          cornerRadius:
            seatType === SeatTypeEnumResponseDto.FoldingSeat ? (seatBox.cornerRadius() as number) : 1,
        });
      }
    }
  }

  private setGroupDefaultState(
    group: Konva.Group,
    seatId: number,
    seatType: SeatTypeEnumResponseDto,
    seatLayer: Konva.Layer,
  ) {
    if (group) {
      const seatBox: Konva.Rect = group.findOne(`#catalog-seat-box-${seatId}`);
      const seatNumber: Konva.Label = seatLayer.findOne(`#catalog-seat-number-${seatId}`);
      const seatHighlighter: Konva.Label = group.findOne(`#catalog-seat-highlighter-${seatId}`);
      if (seatBox) {
        if (seatNumber) {
          seatNumber.getTag().setAttrs({
            fill: 'transparent',
          });
        }
        if (seatHighlighter) {
          seatHighlighter.name('');
          seatHighlighter.setAttrs({
            visible: false,
            name: 'selection-highlighter',
          });
        }
        seatBox.setAttrs({
          width: this.seatSize,
          height: this.seatSize,
          x: 0,
          y: 0,
          cornerRadius:
            seatType === SeatTypeEnumResponseDto.FoldingSeat ? (seatBox.cornerRadius() as number) : 2,
        });
      }
    }
  }

  private getShapePoints(shape: Konva.Line | Konva.Rect | Konva.Group): [number, number][] {
    if (!shape) {
      return [];
    }
    // lasso
    if (shape instanceof Konva.Line) {
      const points = shape.points();
      const result: [number, number][] = [];
      let i = 0;
      while (i < points?.length - 1) {
        result.push([points[i], points[i + 1]]);
        i += 2;
      }
      return result;
      // rect
    } else {
      const { x, y } = shape.getPosition();
      return [
        [x, y],
        [x + shape.width(), y],
        [x + shape.width(), y + shape.height()],
        [x, y + shape.height()],
      ];
    }
  }

  private rotatePoint({ x, y }, rotation: number) {
    const angleRadians = (rotation * Math.PI) / 180;
    const rcos = Math.cos(angleRadians);
    const rsin = Math.sin(angleRadians);
    return { x: x * rcos - y * rsin, y: y * rcos + x * rsin };
  }

  private rotateAroundCenter(node: Konva.Text, letter: PlanBlockLetterResponseDto) {
    const topLeft = { x: (-node.width() * 3) / 5, y: (-node.height() * 3) / 5 };
    const current = this.rotatePoint(topLeft, 0);
    const rotated = this.rotatePoint(topLeft, letter?.rotation);
    const dx = rotated.x - current.x,
      dy = rotated.y - current.y;

    node.rotation(letter?.rotation);
    node.x(node.x() + dx + (16 * (letter?.label?.length - 1)) / 3);
    node.y(node.y() + dy + (16 * (letter?.label?.length - 1)) / 3);
  }

  public getZoomProps(
    stage: Konva.Stage,
    wheelEvent: Konva.KonvaEventObject<WheelEvent>,
    scaleBy: number,
    initialScale: number,
    zoomDirection?: number,
  ): { oldScale: number; newScale: number; position: Vector2d } {
    if (wheelEvent) {
      // stop default scrolling
      wheelEvent.evt.preventDefault();
    }
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    // how to scale? Zoom in? Or zoom out?
    let direction = zoomDirection || (wheelEvent?.evt?.deltaY > 0 ? -1 : 1);
    // when we zoom on trackpad, evt.ctrlKey is true
    // in that case lets revert direction
    if (wheelEvent?.evt?.ctrlKey) {
      direction = -direction;
    }
    let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    // round it to 3 decimals
    newScale = Math.round(newScale * 1000) / 1000;
    const position =
      newScale === initialScale
        ? { x: 0, y: 0 }
        : {
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
          };

    return { oldScale, newScale, position };
  }

  public getPointerPosition(stage: Konva.Stage): Vector2d {
    const pointer = stage.getPointerPosition();
    return {
      x: (pointer.x - stage.x()) / stage.scaleX(),
      y: (pointer.y - stage.y()) / stage.scaleY(),
    };
  }

  /**
   * used to load images only once, and caching them for repetitive use
   */
  private async loadReusableAssets() {
    this.iconsMap.blockedSeatImage = await this.loadAsset(BlockedSeatIcon);
    this.iconsMap.paidSeatImage = await this.loadAsset(PaidIcon);
    this.iconsMap.reservedSeatImage = await this.loadAsset(reservedIcon);
    this.iconsMap.editedSeatImage = await this.loadAsset(editedIcon);
    this.iconsMap.controlledSeatImage = await this.loadAsset(VectorIcon);
    this.iconsMap.subscribedSeatImage = await this.loadAsset(VerifiedIcon);
    if (this.data?.contingents) {
      await Promise.all(
        this.data.contingents
          .filter(({ image }) => !!image)
          .map(async ({ image, id }) => {
            this.iconsMap[id] = await this.loadAsset(`data:image/png;base64,${image}`);
          }),
      );
    }
  }

  /**
   * transforms a base64 image to a canvas element
   */
  private loadAsset(imageSrc: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageSrc;
    });
  }

  public getInitialScale(container: ElementRef, props: CanvasProps) {
    // now we need to fit stage into parent container
    const containerWidth = container?.nativeElement?.offsetWidth;
    // to do this we need to scale the stage
    const initialScaleX = containerWidth / (props?.width + 16);
    // now we need to fit stage into parent
    const containerHeight = container?.nativeElement?.offsetHeight;
    // to do this we need to scale the stage
    const initialScaleY = containerHeight / (props?.height + 16);
    return Math.round(Math.min(initialScaleX, initialScaleY) * 1000) / 1000;
  }

  /**
   * limits the drag event on the stage
   */
  public defineDragBoundaries({ x, y }): Vector2d {
    let newY = y;
    let newX = x;
    // left boundaries
    if (newX > 200) {
      newX = 200;
    }
    // top boundaries
    if (newY > 200) {
      newY = 200;
    }
    return {
      x: newX,
      y: newY,
    };
  }

  public drawSelectionTooltip(initialText: string) {
    return new Konva.Label({
      x: 0,
      y: 0,
      visible: false,
    }).add(
      new Konva.Tag({
        fill: ChartsUtils.getColorByPropertyName('--color-black'),
        pointerWidth: 0,
        pointerHeight: 0,
        lineJoin: 'round',
        shadowColor: 'black',
        shadowBlur: 3,
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        shadowOpacity: 0.5,
        cornerRadius: 2,
      }),
      new Konva.Text({
        id: 'selection-ttp-text',
        text: initialText,
        fontFamily: 'Roboto',
        fontSize: 14,
        padding: 4,
        fill: ChartsUtils.getColorByPropertyName('--color-white'),
      }),
    );
  }

  /**
   * calculates the natural width and height of the plan,
   * based on all the elements coordinates (seats, letters, images)
   */
  public getCanvasProps(elements: { x: number; y: number; [key: string]: any }[]): CanvasProps {
    const maxX = _.max(elements, (element) => element?.x) as any;
    const minX = _.min(elements, (element) => element?.x) as any;
    const maxY = _.max(elements, (element) => element?.y) as any;
    const minY = _.min(elements, (element) => element?.y) as any;
    return {
      width: maxX?.x - minX?.x,
      height: maxY?.y - minY?.y,
      maxX: maxX?.x,
      maxY: maxY?.y,
      minX: minX?.x,
      minY: minY?.y,
    };
  }

  /**
   * getting all the plan elements is useful to calculate the canvas natural dimensions and scaling
   */
  public getAllPlanElements() {
    return [
      ...(this.data?.seats
        ? this.data.seats.map((seat) => ({
            ...seat,
            x: seat?.positionX,
            y: seat?.positionY,
          }))
        : []),
      ...(this.data?.letters ? this.data.letters : []),
      ...(this.data?.imageData?.imageDatas
        ? this.data.imageData?.imageDatas.map((image) => ({
            ...image,
            x: image?.x + image?.width,
            y: image?.y + image?.height,
          }))
        : []),
      ...(this.data?.shapes
        ? this.data.shapes.map((shape) => ({
            ...shape,
            x: shape?.positionX,
            y: shape?.positionY,
          }))
        : []),
    ];
  }

  public applyParentGroupCache(parentGroupId: string, pixelRatio: number) {
    this.parentGroupMap?.[parentGroupId]?.cache({ pixelRatio });
  }

  /**
   * caches the redundant element shapes (rectangle, circle, seat number, etc..)
   * contains the common attributes between elements. when we begin drawing, we only specify:
   * color, stroke and coordinates
   */
  private async prepareShapeCache() {
    this.shapesMap = {
      rect: new Konva.Rect({
        width: this.seatSize,
        height: this.seatSize,
        cornerRadius: 2,
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
      }),
      circle: new Konva.Rect({
        width: this.seatSize,
        height: this.seatSize,
        cornerRadius: this.seatSize,
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
      }),
      number: new Konva.Label({
        x: 0,
        y: 2,
        listening: false,
      }).add(
        new Konva.Tag({
          fill: 'transparent',
          pointerWidth: 0,
          pointerHeight: 0,
          lineJoin: 'round',
          shadowForStrokeEnabled: false,
          cornerRadius: 2,
          listening: false,
        }),
        new Konva.Text({
          text: null,
          fontFamily: 'Roboto',
          fontStyle: 'bold',
          fontSize: 6,
          verticalAlign: 'middle',
          align: 'center',
          hitStrokeWidth: 0,
          shadowForStrokeEnabled: false,
          fill: ChartsUtils.getColorByPropertyName('--color-black'),
          listening: false,
        }),
      ),
      selectionHighlighter: new Konva.Rect({
        name: 'selection-highlighter',
        width: this.seatSize,
        height: this.seatSize,
        fill: 'transparent',
        stroke: ChartsUtils.getColorByPropertyName('--color-primary-600'),
        strokeWidth: 1,
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
        visible: false,
        listening: false,
      }),
    };
    await this.loadReusableAssets();
  }
}
